import { Box, Button, CircularProgress, FormControlLabel, Grid, Switch, TextField, Tooltip, Typography } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import React, { useEffect, useState } from 'react';
import { ButtonWithLoader } from 'components/reusable/button-loader';
import { UserPermissionsForm } from 'components/reusable/user-permissions-form';
import { appConfig } from 'config';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { getSsoConfig, putSsoConfig, putSsoEnabled, putSsoJitDefaults } from 'store/sagas/sagas';
import './saml-security.css';
const IDP_FIELD_TOOLTIP = 'Click to copy';
const IdpField = ({ label, value, onCopy }) => {
    if (!value)
        return null;
    return (React.createElement(Grid, { item: true, xs: 12 },
        React.createElement(Typography, { className: "saml-security__idp-label", variant: "caption" }, label),
        React.createElement(Tooltip, { placement: "top-start", title: IDP_FIELD_TOOLTIP },
            React.createElement(Typography, { className: "saml-security__idp-value", onClick: () => {
                    onCopy(value);
                }, variant: "body2" },
                value,
                React.createElement(FileCopyOutlinedIcon, { className: "saml-security__copy-icon", fontSize: "inherit" })))));
};
export const SamlSecurity = () => {
    const { state, dispatch, asyncDispatch } = useGlobalState();
    const [ssoConfig, setSsoConfig] = useState(null);
    const [metadataUrl, setMetadataUrl] = useState('');
    const [metadataXml, setMetadataXml] = useState('');
    const [xmlError, setXmlError] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [samlEnabled, setSamlEnabled] = useState(false);
    const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState(false);
    const [jitIsReviewer, setJitIsReviewer] = useState(false);
    const [jitIsDownloader, setJitIsDownloader] = useState(false);
    const [jitIsAccountAdmin, setJitIsAccountAdmin] = useState(false);
    const [jitGroupId, setJitGroupId] = useState(null);
    const [isJitDirty, setIsJitDirty] = useState(false);
    const [isJitSaving, setIsJitSaving] = useState(false);
    const accountId = state.account?.id;
    const accountName = state.account?.name ?? '';
    useEffect(() => {
        if (!accountId)
            return;
        asyncDispatch(getSsoConfig(accountId))
            .then((config) => {
            setSsoConfig(config);
            setSamlEnabled(config.enabled);
            setMetadataUrl(config.metadataUrl ?? '');
            setMetadataXml(config.metadataXml ?? '');
            setJitIsReviewer(config.jitDefaultIsReviewer);
            setJitIsDownloader(config.jitDefaultIsDownloader);
            setJitIsAccountAdmin(config.jitDefaultIsAccountAdmin);
            setJitGroupId(config.jitDefaultGroupId);
        })
            .catch(() => {
            setPageError(true);
            dispatch(setMessages({
                value: 'Failed to load SAML configuration.',
                severity: 'error'
            }));
        })
            .finally(() => {
            setPageLoading(false);
        });
    }, [accountId]);
    const handleMetadataUrlChange = (e) => {
        const val = e.target.value;
        setMetadataUrl(val);
        setMetadataXml('');
        setXmlError('');
        setSaveSuccess(false);
        setIsDirty(val !== (ssoConfig?.metadataUrl ?? '') ||
            (ssoConfig?.metadataXml ?? '') !== '');
    };
    const handleMetadataXmlChange = (e) => {
        const val = e.target.value;
        setMetadataXml(val);
        setMetadataUrl('');
        setSaveSuccess(false);
        setIsDirty((ssoConfig?.metadataUrl ?? '') !== '' ||
            val !== (ssoConfig?.metadataXml ?? ''));
        if (val) {
            const doc = new DOMParser().parseFromString(val, 'application/xml');
            const parseError = doc.querySelector('parsererror');
            setXmlError(parseError ? 'Invalid XML' : '');
        }
        else {
            setXmlError('');
        }
    };
    const handleSave = () => {
        if (!accountId || !isDirty || isSaving)
            return;
        setIsSaving(true);
        setSaveSuccess(false);
        const payload = metadataUrl
            ? { metadataUrl, metadataXml: null }
            : { metadataUrl: null, metadataXml };
        asyncDispatch(putSsoConfig(accountId, payload))
            .then((config) => {
            setSsoConfig(config);
            setSamlEnabled(config.enabled);
            setIsDirty(false);
            setSaveSuccess(true);
            dispatch(setMessages({
                value: 'SAML configuration saved',
                severity: 'success'
            }));
        })
            .catch(() => {
            dispatch(setMessages({
                value: 'Failed to save SAML configuration. Please check your metadata and try again.',
                severity: 'error'
            }));
        })
            .finally(() => {
            setIsSaving(false);
        });
    };
    const checkJitDirty = (next) => {
        setIsJitDirty(next.isReviewer !== (ssoConfig?.jitDefaultIsReviewer ?? false) ||
            next.isDownloader !== (ssoConfig?.jitDefaultIsDownloader ?? false) ||
            next.isAccountAdmin !==
                (ssoConfig?.jitDefaultIsAccountAdmin ?? false) ||
            next.groupId !== (ssoConfig?.jitDefaultGroupId ?? null));
    };
    const handleJitSave = () => {
        if (!accountId || !isJitDirty || isJitSaving)
            return;
        setIsJitSaving(true);
        asyncDispatch(putSsoJitDefaults(accountId, {
            isReviewer: jitIsReviewer,
            isDownloader: jitIsDownloader,
            isAccountAdmin: jitIsAccountAdmin,
            groupId: jitGroupId
        }))
            .then((config) => {
            setSsoConfig(config);
            setIsJitDirty(false);
            dispatch(setMessages({ value: 'Security settings saved', severity: 'success' }));
        })
            .catch(() => {
            dispatch(setMessages({
                value: 'Failed to save security settings',
                severity: 'error'
            }));
        })
            .finally(() => {
            setIsJitSaving(false);
        });
    };
    const handleToggleEnabled = (_, checked) => {
        if (!accountId || isTogglingEnabled)
            return;
        if (checked) {
            const hasMetadata = ssoConfig?.metadataUrl ?? ssoConfig?.metadataXml;
            if (!hasMetadata) {
                dispatch(setMessages({
                    value: 'Save valid SAML metadata before enabling SAML.',
                    severity: 'warning'
                }));
                return;
            }
        }
        setIsTogglingEnabled(true);
        asyncDispatch(putSsoEnabled(accountId, checked))
            .then(() => {
            setSamlEnabled(checked);
            dispatch(setMessages({
                value: checked
                    ? `SAML Enabled for ${accountName}`
                    : `SAML Disabled for ${accountName}`,
                severity: 'success'
            }));
        })
            .catch(() => {
            dispatch(setMessages({
                value: checked
                    ? 'Failed to enable SAML. Ensure valid metadata is saved first.'
                    : 'Failed to disable SAML.',
                severity: 'error'
            }));
        })
            .finally(() => {
            setIsTogglingEnabled(false);
        });
    };
    const handleCopy = (value) => {
        navigator.clipboard
            .writeText(value)
            .then(() => {
            dispatch(setMessages({ value: 'Copied to clipboard', severity: 'success' }));
        })
            .catch(console.error);
    };
    const apiBase = appConfig.onboardApi;
    if (pageLoading) {
        return (React.createElement(Box, { alignItems: "center", display: "flex", justifyContent: "center", minHeight: "200px" },
            React.createElement(CircularProgress, null)));
    }
    if (pageError) {
        return (React.createElement(Box, { alignItems: "center", display: "flex", flexDirection: "column", justifyContent: "center", minHeight: "200px" },
            React.createElement(Typography, { color: "error", gutterBottom: true }, "Failed to load SAML configuration."),
            React.createElement(Button, { color: "primary", onClick: () => {
                    window.location.reload();
                }, variant: "outlined" }, "Retry")));
    }
    return (React.createElement("div", { className: "saml-security" },
        React.createElement("div", { className: "saml-security__header" },
            React.createElement("h1", { className: "g-page-name" },
                "SAML Security ",
                accountName && `[${accountName}]`),
            React.createElement(FormControlLabel, { className: "saml-security__toggle", control: React.createElement(Switch, { checked: samlEnabled, color: "primary", disabled: isTogglingEnabled, onChange: handleToggleEnabled }), label: "Enable SAML", labelPlacement: "start" })),
        React.createElement("div", { className: "saml-security__section" },
            React.createElement(Typography, { className: "saml-security__section-title", variant: "h6" }, "SAML Configuration"),
            React.createElement(Grid, { container: true, spacing: 2 },
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(TextField, { fullWidth: true, label: "Metadata URL", onChange: handleMetadataUrlChange, size: "small", value: metadataUrl, variant: "outlined" })),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(TextField, { error: !!xmlError, fullWidth: true, helperText: xmlError, label: "Metadata XML", minRows: 5, multiline: true, onChange: handleMetadataXmlChange, value: metadataXml, variant: "outlined" })),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Box, { alignItems: "center", display: "flex", gridGap: 8 },
                        React.createElement(ButtonWithLoader, { color: "primary", disabled: !isDirty || isSaving || !!xmlError, isLoading: isSaving, name: "Save", onClick: handleSave }),
                        saveSuccess && !isDirty && (React.createElement(CheckCircleOutlineIcon, { className: "saml-security__save-success-icon", color: "primary" })))))),
        React.createElement("div", { className: "saml-security__section" },
            React.createElement(Typography, { className: "saml-security__section-title", variant: "h6" }, "User Defaults"),
            React.createElement(Typography, { className: "saml-security__idp-description", variant: "body2" }, "Future users added through \u201CJust in Time\u201D provisioning."),
            React.createElement(UserPermissionsForm, { groupId: jitGroupId, groups: state.accountGroups ?? [], hideAdmin: true, isAccountAdmin: jitIsAccountAdmin, isDownloader: jitIsDownloader, isReviewer: jitIsReviewer, onAdminChange: (checked) => {
                    setJitIsAccountAdmin(checked);
                    checkJitDirty({
                        isReviewer: jitIsReviewer,
                        isDownloader: jitIsDownloader,
                        isAccountAdmin: checked,
                        groupId: jitGroupId
                    });
                }, onDownloaderChange: (checked) => {
                    setJitIsDownloader(checked);
                    checkJitDirty({
                        isReviewer: jitIsReviewer,
                        isDownloader: checked,
                        isAccountAdmin: jitIsAccountAdmin,
                        groupId: jitGroupId
                    });
                }, onGroupChange: (val) => {
                    setJitGroupId(val);
                    checkJitDirty({
                        isReviewer: jitIsReviewer,
                        isDownloader: jitIsDownloader,
                        isAccountAdmin: jitIsAccountAdmin,
                        groupId: val
                    });
                }, onReviewerChange: (checked) => {
                    setJitIsReviewer(checked);
                    checkJitDirty({
                        isReviewer: checked,
                        isDownloader: jitIsDownloader,
                        isAccountAdmin: jitIsAccountAdmin,
                        groupId: jitGroupId
                    });
                }, showTooltips: true }),
            React.createElement(Box, { mt: 3 },
                React.createElement(ButtonWithLoader, { color: "primary", disabled: !isJitDirty || isJitSaving, isLoading: isJitSaving, name: "Save", onClick: handleJitSave }))),
        React.createElement("div", { className: "saml-security__section" },
            React.createElement(Typography, { className: "saml-security__section-title", variant: "h6" }, "IdP Configuration"),
            React.createElement(Typography, { className: "saml-security__idp-description", variant: "body2" }, "Configure these values in your Identity Provider."),
            React.createElement(Grid, { className: "saml-security__idp-fields", container: true, spacing: 1 },
                React.createElement(IdpField, { label: "Entity ID / Audience URL", onCopy: handleCopy, value: ssoConfig?.spEntityId ?? null }),
                React.createElement(IdpField, { label: "Issuer ID", onCopy: handleCopy, value: ssoConfig?.spEntityId ?? null }),
                React.createElement(IdpField, { label: "ACS URL / Reply URL (Single sign-on URL in Okta)", onCopy: handleCopy, value: ssoConfig?.acsUrl ?? null }),
                React.createElement(IdpField, { label: "Sign-On URL", onCopy: handleCopy, value: ssoConfig?.signOnUrl ?? null }),
                React.createElement(IdpField, { label: "Name ID Format", onCopy: handleCopy, value: "EmailAddress" }),
                React.createElement(IdpField, { label: "Attribute mapping", onCopy: handleCopy, value: 'firstName to user.firstName\nlastName to user.lastName' }),
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Button, { className: "saml-security__download-btn", component: "a", href: `${apiBase}/saml/metadata`, rel: "noreferrer", size: "small", target: "_blank", variant: "outlined" }, "Download XML"))))));
};
